import React, { useState } from "react";
import { View, Text, Modal, ScrollView, Pressable, TextInput } from "react-native";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "@/elements/components/ui/button";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useConvexAction } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import { api } from "../utils/convex-api";

// Initialize Stripe - you'll need to get this from your env vars
const stripePromise = loadStripe(process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

interface StripePaymentFormProps {
  clientSecret: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onClose?: () => void;
  originalAmount: number;
  purchaseOrderId: string;
  paymentIntentId: string;
  returnTo?: string;
  bookingData?: string;
}

function PaymentForm({ onSuccess, onError, onClose, originalAmount, purchaseOrderId, paymentIntentId, returnTo, bookingData }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<string>("");
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [discount, setDiscount] = useState<{ amount: number; code: string; finalAmount: number } | null>(null);
  const router = useRouter();

  const finalAmount = discount?.finalAmount || originalAmount;

  const { mutateAsync: updatePaymentIntent } = useMutation({
    mutationFn: useConvexAction(api.parent.buyCredits.actions.updatePaymentIntentWithPromotion),
  });

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) return;
    
    setIsValidatingPromo(true);
    setErrorMessage(null);
    
    try {
      const result = await updatePaymentIntent({
        paymentIntentId: paymentIntentId,
        promotionCode: promoCode.trim(),
        originalAmount: originalAmount,
      });
      
      if (result.success) {
        setDiscount({
          amount: result.discount.amount,
          code: result.discount.code,
          finalAmount: result.finalAmount,
        });
      } else {
        setErrorMessage("Invalid or expired promotion code");
        setDiscount(null);
      }
    } catch (error) {
      setErrorMessage("Invalid promotion code");
      setDiscount(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleSubmit = async () => {

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const returnParams = new URLSearchParams({ success: "true" });
      if (returnTo) returnParams.append("returnTo", returnTo);
      if (bookingData) returnParams.append("bookingData", bookingData);
      
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/(app)/buy-credits?${returnParams.toString()}`,
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(error.message || "An error occurred");
        onError?.(error.message || "An error occurred");
      } else {
        // Payment succeeded
        onSuccess?.();
        const params = new URLSearchParams({ success: "true" });
        if (returnTo) params.append("returnTo", returnTo);
        if (bookingData) params.append("bookingData", bookingData);
        router.replace(`/(app)/buy-credits?${params.toString()}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setErrorMessage(message);
      onError?.(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      visible={true}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <View className="flex-1 bg-teachLoop-creamBase">
        {/* Header */}
        <View className="flex-row items-center justify-between p-5 border-b border-black border-opacity-10">
          <View className="flex-1">
            <Text className="text-xl font-geologica-medium font-medium text-black leading-[1.5]">
              Complete Payment
            </Text>
            {discount ? (
              <View>
                <Text className="text-base font-geologica-light font-normal text-gray-500 leading-[1.5] line-through">
                  AED {originalAmount / 100}
                </Text>
                <Text className="text-base font-geologica-medium font-medium text-green-600 leading-[1.5]">
                  AED {finalAmount / 100} (Code: {discount.code})
                </Text>
              </View>
            ) : (
              <Text className="text-base font-geologica-light font-normal text-gray-600 leading-[1.5]">
                AED {finalAmount / 100}
              </Text>
            )}
          </View>
          <Pressable 
            onPress={onClose}
            className="w-8 h-8 items-center justify-center"
          >
            <Ionicons name="close" size={24} color="#000" />
          </Pressable>
        </View>

        <ScrollView className="flex-1">
          <View className="p-5 gap-6">
            {/* Payment Form */}
            <View className="px-4 py-6 bg-teachLoop-creamLight rounded-lg border" style={{ borderColor: "rgba(239, 233, 230, 1)" }}>
              <PaymentElement 
                options={{
                  layout: {
                    type: 'tabs',
                    defaultCollapsed: false,
                  },
                  fields: {
                    billingDetails: 'auto',
                  },
                  terms: {
                    card: 'auto',
                  },
                }}
              />
            </View>

            {/* Promo Code Section */}
            <View className="px-4 py-4 bg-teachLoop-creamDark rounded-lg border" style={{ borderColor: "rgba(239, 233, 230, 1)" }}>
              <Pressable 
                onPress={() => setShowPromoInput(!showPromoInput)}
                className="flex-row items-center justify-between"
              >
                <Text className="text-base font-geologica-medium font-medium text-black leading-[1.5]">
                  Have a promo code?
                </Text>
                <Ionicons 
                  name={showPromoInput ? "chevron-up" : "chevron-down"} 
                  size={20} 
                  color="#000" 
                />
              </Pressable>
              
              {showPromoInput && (
                <View className="mt-3 gap-3">
                  <View className="flex-row gap-2">
                    <TextInput
                      value={promoCode}
                      onChangeText={(text) => {
                        setPromoCode(text);
                        setDiscount(null); // Clear previous discount when typing
                        setErrorMessage(null);
                      }}
                      placeholder="Enter promo code"
                      className="flex-1 bg-white rounded-lg border px-3 py-3 text-base font-geologica-medium"
                      style={{ borderColor: "rgba(239, 233, 230, 1)" }}
                      placeholderTextColor="#9CA3AF"
                    />
                    <Button
                      size="sm"
                      intent="secondary"
                      disabled={!promoCode.trim() || isValidatingPromo}
                      onPress={handlePromoCodeValidation}
                    >
                      <Button.Text>
                        {isValidatingPromo ? "..." : "Apply"}
                      </Button.Text>
                    </Button>
                  </View>
                  
                  {discount && (
                    <View className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <Text className="text-green-700 text-sm font-geologica-medium">
                        ✓ Code "{discount.code}" applied! Save AED {discount.amount / 100}
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>
            
            {errorMessage && (
              <View className="px-4 py-3 bg-red-50 rounded-lg border border-red-200">
                <Text className="text-red-600 text-sm font-geologica-medium font-medium leading-[1.5]">
                  {errorMessage}
                </Text>
              </View>
            )}

            {/* Payment Button */}
            <Button
              size="md"
              intent="secondary"
              className="w-full"
              disabled={!stripe || !elements || isProcessing}
              onPress={handleSubmit}
            >
              <Button.Text>
                {isProcessing ? "Processing..." : `Pay AED ${finalAmount / 100}`}
              </Button.Text>
            </Button>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

export default function StripePaymentForm({
  clientSecret,
  onSuccess,
  onError,
  onClose,
  originalAmount,
  purchaseOrderId,
  paymentIntentId,
  returnTo,
  bookingData,
}: StripePaymentFormProps) {
  const options = {
    clientSecret,
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: '#0F172A', // Dark color matching the app
        colorBackground: '#FEF7F4', // teachLoop-creamLight
        colorText: '#000000',
        borderRadius: '8px',
        fontFamily: 'Geologica, system-ui, sans-serif',
      },
    },
    loader: 'auto' as const,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
        onClose={onClose}
        originalAmount={originalAmount}
        purchaseOrderId={purchaseOrderId}
        paymentIntentId={paymentIntentId}
        returnTo={returnTo}
        bookingData={bookingData}
      />
    </Elements>
  );
}